import { createAsyncThunk } from "@reduxjs/toolkit";

import { getErrorMessage } from "@fitness-app/utils";

import { type ActionParams, type AsyncThunkCreator } from "../../../index";
import { PRODUCT_CLIENT_REDUCER } from "../types";

type Payload = {
  invoiceRequest: boolean;
  productId: string;
};

/**
 * The client flipping her own invoice preference (TRE-2145).
 *
 * Separate from `updateInvoiceRequest`, which the admin panel uses: that one
 * writes the table directly and is gated by the staff UPDATE policy added in
 * TRE-2143. A client has no such policy, and giving her a row-scoped one would
 * hand her every column of her own row — access period included. The RPC is the
 * column-scoped version of the same permission, so this path goes through it.
 */
export const updateOwnInvoiceRequest = createAsyncThunk<void, ActionParams<Payload>, AsyncThunkCreator<string>>(
  `${PRODUCT_CLIENT_REDUCER}/updateOwnInvoiceRequest`,
  async ({ payload, onSuccess, onFailure }, { rejectWithValue, extra: { db } }) => {
    try {
      const { data, error } = await db.rpc("set_own_invoice_request", {
        p_product_id: payload.productId,
        p_invoice_requested: payload.invoiceRequest,
      });

      if (error) {
        onFailure?.();

        return rejectWithValue(error.message);
      }

      // False means no row of hers matched the product — worth surfacing rather
      // than leaving the switch to snap back silently, which is the bug this
      // replaces.
      if (!data) {
        onFailure?.();

        return rejectWithValue("No product client row was updated");
      }

      onSuccess?.();
    } catch (e) {
      onFailure?.();

      return rejectWithValue(getErrorMessage(e));
    }
  },
);
